import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Admin Service
 * Handles all admin-related API calls
 */
class AdminService {
  // ==================== USER MANAGEMENT ====================

  /**
   * Get all users
   * @param {Object} params - Query parameters (page, page_size, filters)
   * @returns {Promise} API response
   */
  async getUsers(params = {}) {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN.USERS, params);
    return response;
  }

  /**
   * Get specific user details
   * @param {string} userId - User ID
   * @returns {Promise} API response
   */
  async getUser(userId) {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN.USER_DETAIL(userId));
    return response;
  }

  /**
   * Update user details
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise} API response
   */
  async updateUser(userId, userData) {
    const response = await apiClient.patch(
      API_ENDPOINTS.ADMIN.USER_DETAIL(userId),
      userData
    );
    return response;
  }

  /**
   * Delete user
   * @param {string} userId - User ID
   * @returns {Promise} API response
   */
  async deleteUser(userId) {
    const response = await apiClient.delete(
      API_ENDPOINTS.ADMIN.USER_DETAIL(userId)
    );
    return response;
  }

  /**
   * Get user activity logs
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getUserActivity(userId, params = {}) {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN.USER_ACTIVITY(userId),
      params
    );
    return response;
  }

  /**
   * List datasets available for field-access management
   * @returns {Promise} API response
   */
  async getFieldAccessDatasets() {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN.FIELD_ACCESS_DATASETS);
    return response;
  }

  /**
   * Get field-access details for a dataset
   * @param {string} datasetKey - Dataset key
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getFieldAccessDetail(datasetKey, params = {}) {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN.FIELD_ACCESS_DETAIL(datasetKey),
      params
    );
    return response;
  }

  /**
   * Save allowed fields for a dataset
   * @param {string} datasetKey - Dataset key
   * @param {string[]} allowedFields - Allowed field names
   * @returns {Promise} API response
   */
  async updateFieldAccess(datasetKey, allowedFields) {
    const response = await apiClient.put(
      API_ENDPOINTS.ADMIN.FIELD_ACCESS_DETAIL(datasetKey),
      { allowed_fields: allowedFields }
    );
    return response;
  }

  /**
   * Reset a dataset back to default field access
   * @param {string} datasetKey - Dataset key
   * @returns {Promise} API response
   */
  async resetFieldAccess(datasetKey) {
    const response = await apiClient.delete(
      API_ENDPOINTS.ADMIN.FIELD_ACCESS_DETAIL(datasetKey)
    );
    return response;
  }

  // ==================== SYSTEM MANAGEMENT ====================

  /**
   * Get system health status
   * @returns {Promise} API response
   */
  async getSystemHealth() {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN.SYSTEM_HEALTH);
    return response;
  }

  /**
   * Get system statistics
   * @param {Object} params - Query parameters (time_range, etc.)
   * @returns {Promise} API response
   */
  async getSystemStats(params = {}) {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN.SYSTEM_STATS,
      params
    );
    return response;
  }

  /**
   * Get system logs
   * @param {Object} params - Query parameters (level, page, etc.)
   * @returns {Promise} API response
   */
  async getSystemLogs(params = {}) {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN.SYSTEM_LOGS,
      params
    );
    return response;
  }

  /**
   * Get a single system log file
   * @param {string} group - Log folder name
   * @param {string} collection - Log collection name
   * @returns {Promise} API response
   */
  async getSystemLog(group, collection) {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN.SYSTEM_LOG_DETAIL(
        encodeURIComponent(group),
        encodeURIComponent(collection)
      )
    );
    return response;
  }

  /**
   * Clear a single system log file
   * @param {string} group - Log folder name
   * @param {string} collection - Log collection name
   * @returns {Promise} API response
   */
  async deleteSystemLog(group, collection) {
    const response = await apiClient.delete(
      API_ENDPOINTS.ADMIN.SYSTEM_LOG_DETAIL(
        encodeURIComponent(group),
        encodeURIComponent(collection)
      )
    );
    return response;
  }

  /**
   * Get activity logs
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getActivityLogs(params = {}) {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN.ACTIVITY_LOGS,
      params
    );
    return response;
  }
}

const adminService = new AdminService();

export default adminService;
